import { ftToCm, inToCm, mToIn, cmToIn, kgToLb, lbToKG, heightConverter, cmToMCm, mCmToFtIn } from "Helpers/util";
import { hotjarEvent } from "Helpers/helper";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { apiCalculateMacros } from "Root/api/apiProfile";
import { setDietitianStoreAction, setGlobalLoading, setMacroResult, } from "Root/profile/redux/actionCreators/makeActionCreator";
import { LoadRegisteredDietitianThunk, LoadMacroAnswersThunk, } from "Root/profile/redux/thunkCreators/dietitian/registeredDietitian";
import { fetchProfileDataThunk } from "Root/profile/redux/thunkCreators/myProfile/profileThunk";
import LoadProfileInfoThunk from "Root/profile/redux/thunkCreators/user/user";
import toastService from "Root/profile/toastService";
import { BASE, MACRO_TOOL_RESULTS, RD_TOOL } from "Root/profile/ProfileRoutes";
const faqs = [
    {
        title: "Do I need to track my macros/calories to be successful with my goals?",
        text: "Tracking macros and calories is not the only way to be successful in achieving your goals. While it can be a helpful and accurate way to ensure your nutritional needs are met, your personal preferences, lifestyle, and any history with disordered eating/thoughts should be considered before taking this approach. Alternatives to tracking include: intuitive eating, following the MyPlate method at meals, monitoring portions, following the 80/20 rule, and so much more!",
    },
    {
        title: "How do I start tracking my macros?",
        text: "Once you have your targets determined, you can begin tracking. This can be done manually using pen and paper, through a spreadsheet, or with the help of a mobile app or online tracking tool. Many apps, such as MyFitnessPal or Lose It!, allow you to log your food and track your macronutrient intake conveniently. Your outcomes of tracking will only be as accurate as your inputs, so be as detailed and accurate as possible in recording the amounts of everything you eat and drink.",
    },
    {
        title: "How long should I track my macros/calories for?",
        text: "Our team of RDs recommend tracking your calories/macros for a few weeks at a time. Tracking can be a time-consuming, cumbersome task that may interfere with elements of your social, personal, and professional life, so it’s important to either do so with flexibility or for short periods at a time. Once those few weeks have passed, you can take what you’ve learned from tracking to make qualitative improvements to your habits moving forward.",
    },
    {
        title: "Can I just track my calories?",
        text: "Yes, of course! If you want to be more lenient with your tracking, you can choose to just track calories. Calories will have the most impact on weight management, and if you make balanced and nutrient-dense food choices most of the time, your macros may already fall into appropriate ranges.",
    },
    {
        title: "Why are my carb recommendations so high?",
        text: "We recommend carbohydrate amounts based on your performance and body composition goals in conjunction with the general dietary guidelines and considerations for overall health and energy needs. Around 45-65% of calories from carbohydrates is the current guideline. We do offer low carb and keto macronutrient preferences for those who wish to go below that guideline. Select “RECALCULATE” at the bottom of this page if you’d like to change up your preferences.",
    },
    {
        title: "My protein recommendations seem low?",
        text: "The amount of protein that was recommended for you is based on your specific goals and information. Getting enough protein is really important, but you don’t have to consume more than your body needs. Generally speaking, research consistently shows that anywhere between 1.2-1.7 grams of protein per kg of body weight is plenty to support muscle maintenance and growth in healthy individuals. If you prefer going higher on your protein intake though, that’s okay!",
    },
    {
        title: "Should I have different calorie/macro goals on rest days vs. exercise days?",
        text: "Not necessarily. Your activity level is already factored into this calculation, so rather than adjusting your intake up or down on workout days vs. rest days, you can simply aim to be consistent with your current activity level and calorie/macro intake on a daily and weekly basis.",
    },
    {
        title: "Why are these recommendations different from other macro calculators that I’ve used?",
        text: "You may see different numbers on other macronutrient calculators. This could be due to other tools using different percentages, equations, or giving specific numbers rather than ranges. There are different opinions on recommendations for macronutrients, but our macronutrient tool was created by our dietitians with your info and goals in mind. If you feel like your macro suggestions are not accurate to you, please reach out to your Gainful registered dietitian, and they will take a second pass at it and personally re-calculate your needs.",
    },
];
const useRdMacroTool = () => {
    const profileData = useSelector((state) => state.myProfile.originalAnswers);
    const macroAnswers = useSelector((state) => state.dietitian.macroAnswers);
    const dietitanReduxStoreIsEmpty = useSelector((state) => state.checkStore.dietitian);
    const user = useSelector((state) => state.user);
    const [showModal, setShowModal] = useState(false);
    const [isValid, setIsValid] = useState(true);
    const [hasResult, setHasResult] = useState(false);
    const [resultData, setResultData] = useState({
        calories: 0,
        maintenance_calories: 0,
        carbs: 0,
        fat: 0,
        protein: 0,
        carb_percent: 0,
        fat_percent: 0,
        protein_percent: 0,
        bmi: 0,
        fat_range: [0, 0],
        carbs_range: [0, 0],
        protein_range: [0, 0],
        low_bmi_alert: false,
        goal: "",
    });
    const history = useHistory();
    const [metrics, setMetrics] = useState(false);
    const [values, setValues] = useState({
        gender: "",
        age: "",
        activity_level: "",
        goal: "",
        ft: "",
        inch: "",
        m: "",
        cm: "",
        weight: "",
        weightKg: "",
        goalWeight: "",
        goalWeightKg: "",
        preferences: "balanced",
    });
    const dispatch = useDispatch();
    const processStoredAnswers = (answers) => {
        const full_cm = inToCm(answers.height);
        const [m, cm] = cmToMCm(full_cm);
        const [ft, inch] = mCmToFtIn(m, cm);
        return {
            gender: answers.gender,
            age: answers.age,
            activity_level: answers.activity_level,
            goal: answers.goal,
            ft: ft.toString(),
            inch: inch.toString(),
            m: m.toString(),
            cm: cm.toString(),
            weight: answers.weight,
            weightKg: lbToKG(answers.weight).toString(),
            goalWeight: answers.goal_weight,
            goalWeightKg: lbToKG(answers.goal_weight).toString(),
            preferences: answers.preference,
        };
    };
    useEffect(() => {
        if (macroAnswers) {
            setValues(processStoredAnswers(macroAnswers));
        }
    }, [macroAnswers]);
    useEffect(() => {
        let goal = "";
        if (profileData.reasons?.indexOf("Build Muscle") >= 0 && profileData.reasons?.indexOf("Lose Weight") >= 0) {
            goal = "build muscle";
        }
        else if (profileData.reasons?.indexOf("Lose Weight") >= 0) {
            goal = "lose weight";
        }
        let age = "";
        if (profileData.age) {
            age = profileData.age;
        }
        else if (profileData.age_bucket) {
            age = profileData.age_bucket.split("-")[0].replace("+", "");
        }
        let activity_level = "";
        const weeklyFreq = parseInt(profileData.weeklyFreq1) || profileData.weeklyFreq;
        if (weeklyFreq) {
            if (weeklyFreq <= 1) {
                activity_level = "sedentary";
            }
            else if (weeklyFreq <= 3) {
                activity_level = "slight";
            }
            else if (weeklyFreq <= 5) {
                activity_level = "moderate";
            }
            else if (weeklyFreq <= 7) {
                activity_level = "very";
            }
            else if (weeklyFreq <= 10) {
                activity_level = "extra";
            }
        }
        setValues({
            gender: profileData.gender?.toLowerCase() || "",
            age: age,
            activity_level: activity_level,
            goal: goal,
            ft: profileData.ft,
            inch: profileData.inch,
            m: profileData.m,
            cm: profileData.cm,
            weight: profileData.weight?.toString(),
            weightKg: profileData.weightKG?.toString(),
            goalWeight: profileData.goalWeight?.toString(),
            goalWeightKg: profileData.goalWeightKG?.toString(),
            preferences: profileData.dietary.includes("Keto") ? "keto" : "balanced",
        });
    }, [profileData]);
    // useEffect(() => {
    //   if (values.goal === "lose weight" && values.weight < values.goalWeight) {
    //     setValues((prevState) => ({
    //       ...prevState,
    //       goal: "",
    //     }));
    //   }
    //   if (values.goal === "gain weight" && values.weight > values.goalWeight) {
    //     setValues((prevState) => ({
    //       ...prevState,
    //       goal: "",
    //     }));
    //   }
    // }, [values]);
    const handleWeightInput = (e) => {
        setValues((prevState) => ({
            ...prevState,
            weight: e,
            weightKg: lbToKG(e).toString(),
        }));
    };
    const handleWeightKgInput = (e) => {
        setValues((prevState) => ({
            ...prevState,
            weightKg: e,
            weight: kgToLb(e).toString(),
        }));
    };
    const handleGoalWeightInput = (e) => {
        setValues((prevState) => ({
            ...prevState,
            goalWeight: e,
            goalWeightKg: lbToKG(e).toString(),
        }));
    };
    const handleGoalWeightKgInput = (e) => {
        setValues((prevState) => ({
            ...prevState,
            goalWeightKg: e,
            goalWeight: kgToLb(e).toString(),
        }));
    };
    const onChangeValue = (value, name) => {
        setValues({
            ...values,
            [name]: value,
        });
    };
    const goToRd = () => {
        history.push("/app/dietitian");
    };
    const goToMacroInput = () => {
        history.push(BASE + RD_TOOL);
    };
    const ftInput = (e) => {
        const cmFt = ftToCm(e);
        const cmIn = inToCm(values.inch);
        const totalCm = cmFt + cmIn;
        const m = Math.floor(totalCm / 100);
        const cm = Math.floor(totalCm - m * 100);
        setValues((prevState) => ({
            ...prevState,
            ft: e,
            m: m.toString(),
            cm: cm.toString(),
        }));
    };
    const inInput = (e) => {
        const cmFt = ftToCm(values.ft);
        const cmIn = inToCm(e);
        const totalCm = cmFt + cmIn;
        const m = Math.floor(totalCm / 100);
        const cm = Math.floor(totalCm - m * 100);
        setValues((prevState) => ({
            ...prevState,
            inch: e,
            m: m.toString(),
            cm: cm.toString(),
        }));
    };
    const mInput = (e) => {
        const mIn = mToIn(e);
        const cmIn = cmToIn(values.cm);
        const totalIn = mIn + cmIn;
        const realFeet = Math.floor(totalIn / 12);
        const realInches = Math.floor(totalIn - realFeet * 12);
        setValues((prevState) => ({
            ...prevState,
            m: e,
            ft: realFeet.toString(),
            in: realInches.toString(),
        }));
    };
    const cmInput = (e) => {
        const mIn = mToIn(values.m);
        const cmIn = cmToIn(e);
        const totalIn = mIn + cmIn;
        const realFeet = Math.floor(totalIn / 12);
        const realInches = Math.floor(totalIn - realFeet * 12);
        setValues((prevState) => ({
            ...prevState,
            cm: e,
            ft: realFeet.toString(),
            in: realInches.toString(),
        }));
    };
    const intensityTypes = [
        {
            label: "0-1/Week",
            title: "Sedentary",
            subtitle: "Rarely perform activities that require much physical effort",
            value: "sedentary",
        },
        {
            label: "1-3/Week",
            title: "Lightly Active",
            subtitle: "Exercising a few days a week, where you can talk and sing during training without much effort",
            value: "slight",
        },
        {
            label: "3-5/Week",
            title: "Moderately Active",
            subtitle: "Exercising 3-5 days a week, you can talk fairly comfortably during training but not sing",
            value: "moderate",
        },
        {
            label: "6-7/Week",
            title: "Very Active",
            subtitle: "Exercising almost daily at a strenuous level where you can’t say a few words without gasping for breathe",
            value: "very",
        },
        {
            label: "6-7/Week",
            title: "Extremely Active",
            subtitle: "Exercising at least once daily for 60+ mins at an intense level where you can’t say a few words without gasping for breathe",
            value: "extra",
        },
    ];
    const goals = [
        {
            title: "Gain Weight",
            value: "gain weight",
            disabled: false,
        },
        {
            title: "Lose Weight",
            value: "lose weight",
            disabled: false,
        },
        {
            title: "Maintain Weight",
            value: "maintain muscle",
            disabled: false,
        },
        {
            title: "Build Muscle & Lose Weight",
            value: "build muscle lose weight",
            disabled: false,
        },
    ];
    const preferences = [
        {
            title: "Balanced",
            subtitle: "Balanced macros give appropriate and healthy ranges for each nutrient, and in general can be effective for any goal. ",
            value: "balanced",
        },
        {
            title: "Keto",
            subtitle: "High amount of fat, moderate protein, and very low carbs.",
            value: "keto",
        },
        {
            title: "High protein/Low carb",
            subtitle: "High in protein, relatively low in carbs, and moderate in fat.",
            value: "high_protein_low_carb",
        },
        {
            title: "High fat/Low carb",
            subtitle: "High amount of fat, relatively low in carbs and moderate amount of protein. ",
            value: "high_fat_low_carb",
        },
        {
            title: "High protein",
            subtitle: "High amount of protein with moderate amounts of fat and carbs. ",
            value: "high_protein",
        },
        {
            title: "Endurance training",
            subtitle: "Higher amount of carbohydrates to support longer training sessions, while keeping protein moderate and fat relatively low.",
            value: "endurance_training",
        },
    ];
    const validateField = (field) => {
        const { age, gender, weight, weightKg, ft, m, activity_level, goal, goalWeight, preferences } = values;
        if (field === "age") {
            return Number(age) >= 18 && Number(age) < 115;
        }
        if (field === "weight") {
            return Number(weight) > 50 && Number(weight) < 450;
        }
        if (field === "goalWeight") {
            return Number(goalWeight) > 50 && Number(goalWeight) < 450;
        }
        if (field === "height") {
            return Number(ft) > 2 && Number(ft) <= 7;
        }
        return true;
    };
    const validateForm = () => {
        const { age, gender, weight, weightKg, ft, m, activity_level, goal, goalWeight, preferences } = values;
        if (age && gender && weightKg && m && activity_level && goal && preferences) {
            return true;
        }
        return false;
    };
    const handleClose = () => {
        setShowModal(false);
    };
    const loadData = async () => {
        await dispatch(LoadMacroAnswersThunk());
        if (!macroAnswers) {
            dispatch(fetchProfileDataThunk());
        }
    };
    useEffect(() => {
        if (dietitanReduxStoreIsEmpty) {
            dispatch(LoadRegisteredDietitianThunk());
            dispatch(setDietitianStoreAction(false));
        }
        dispatch(LoadProfileInfoThunk());
        loadData();
    }, []);
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [hasResult]);
    const calculate = async (closeModal = undefined) => {
        const validForm = validateForm();
        setIsValid(validForm);
        if (!validForm) {
            toastService.errorMessage("Please fill all fields.");
            return;
        }
        if (!validateField("age")) {
            toastService.errorMessage("Invalid age: must be at least 18");
            return;
        }
        if (!validateField("weight")) {
            toastService.errorMessage("Invalid weight");
            return;
        }
        if (!validateField("goalWeight")) {
            toastService.errorMessage("Invalid goal weight");
            return;
        }
        if (!validateField("height")) {
            toastService.errorMessage("Invalid height");
            return;
        }
        hotjarEvent("calculate_macros");
        dispatch(setGlobalLoading(true));
        const body = {
            age: values.age,
            gender: values.gender,
            weight: values.weight,
            height: heightConverter(values.ft, values.inch || "0"),
            activity_level: values.activity_level,
            goal: values.goal.toLowerCase(),
            goal_weight: values.goalWeight,
            preference: values.preferences,
        };
        const response = await apiCalculateMacros(body);
        await dispatch(setMacroResult(response));
        if (typeof closeModal === "function") {
            closeModal(response);
        }
        else {
            history.push(BASE + MACRO_TOOL_RESULTS);
        }
        dispatch(setGlobalLoading(false));
    };
    return {
        mInput,
        cmInput,
        inInput,
        ftInput,
        metrics,
        setMetrics,
        values,
        setValues,
        onChangeValue,
        handleWeightInput,
        handleWeightKgInput,
        intensityTypes,
        goals,
        validateForm,
        hasResult,
        setHasResult,
        goToRd,
        open: showModal,
        handleClose,
        calculate,
        resultData,
        setShowModal,
        handleGoalWeightInput,
        handleGoalWeightKgInput,
        isValid,
        user,
        preferences,
        faqs,
        goToMacroInput,
        profileData,
    };
};
export default useRdMacroTool;
